import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useFetchData } from 'services/api/useFetchData';
import { useModal } from 'components/modals/ModalContext';
import { useSettings } from 'services/settings/SettingsContext';
import { useAllowedRight, useViewOnlyPackageRestriction } from 'services/permissions/permissionChecks';
import { saveData } from 'services/api/saveData';
import { PlatformUpdate } from '../contacts/newsletters/NewsletterTypes';
import PlatformUpdateModal from './PlatformUpdateModal';
import { EnvironmentNotificationType } from 'components/layout/NotificationBell';
import { useMentions } from 'components/layout/useMentions';
import { formatDate, formatDateTime } from 'services/utils/dateTimeUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRocket, faTriangleExclamation, faXmark, faAt } from '@fortawesome/free-solid-svg-icons';

/*
 * FocusPanelActivity.tsx
 * Compact, timeline styled activity feed inside the focus panel. It shows
 * the notes in which colleagues tagged the user, the open environment
 * notifications and the latest platform updates. Notable domain events
 * (job completed, invoice paid, etc) will be added in a later phase.
 */

const MAX_ACTIVITY_ITEMS = 5;

const FocusPanelActivity: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { initializeModal } = useModal();
    const { userLocale, userTimezone } = useSettings();
    const canChangeSettings = useAllowedRight('change_settings');
    const [updates, setUpdates] = useState<PlatformUpdate[]>([]);
    const [notifications, setNotifications] = useState<EnvironmentNotificationType[]>([]);
    const { mentions, openMention, dismissMention } = useMentions();

    // Determine whether the view only package hides the platform updates
    const viewOnlyRestricted = useViewOnlyPackageRestriction();

    // Fetch the latest platform updates, not for the view only package
    const { response } = useFetchData({ apiUrl: viewOnlyRestricted ? '' : 'get_platform_update_list' });

    // Fetch the open environment notifications for users who can change settings
    const { response: notificationsResponse } = useFetchData({ apiUrl: canChangeSettings ? 'get_environment_notification_list' : '' });

    // Set the platform updates once the response comes in
    React.useEffect(() => {
        if (response && !viewOnlyRestricted) {
            setUpdates(response.results.slice(0, MAX_ACTIVITY_ITEMS));
        }
    }, [response]);

    // Set the notifications once the response comes in
    React.useEffect(() => {
        if (notificationsResponse) {
            setNotifications(notificationsResponse.results ?? []);
        }
    }, [notificationsResponse]);

    // Open the modal with the content of a platform update
    const openPlatformUpdate = (update: PlatformUpdate) => {
        initializeModal(<PlatformUpdateModal updateId={update.id} />, { modalSize: 'large' });
    };

    // Dismiss a notification so it disappears from the panel
    const dismissNotification = async (event: React.MouseEvent, notificationId: number) => {
        event.stopPropagation();
        try {
            await saveData({
                apiUrl: `dismiss_environment_notification/${notificationId}`,
                method: 'post',
                data: {}
            });
            setNotifications(current => current.filter(notification => notification.id !== notificationId));
        } catch (error) {
            console.error('Error dismissing notification', error);
        }
    };

    // Navigate to the link of a notification
    const openNotification = (notification: EnvironmentNotificationType) => {
        if (notification.link) {
            history.push(notification.link);
        }
    };

    return (
        <div className='focus-panel-activity dashboard-card'>
            <h5 className='title'>{t('dashboard.general.activities_title')}</h5>
            {mentions.length > 0 && (
                <div className='activity-timeline'>
                    {mentions.map(mention => (
                        <div className={`activity-item mention no-drag ${mention.read ? '' : 'unread'}`} key={`mention-${mention.id}`} onClick={() => openMention(mention)}>
                            <div className='activity-marker mention'>
                                <FontAwesomeIcon icon={faAt} />
                            </div>
                            <div className='activity-content'>
                                <span className='activity-title'>
                                    {mention.mentioned_by_name
                                        ? t('notifications.mention.title', { name: mention.mentioned_by_name })
                                        : t('notifications.mention.title_unknown')}
                                </span>
                                <span className='activity-page'>
                                    {mention.page_type ? t(`${mention.page_type}.general.object_name.singular`) : t(`notifications.mention.source_${mention.source}`)} {mention.page_name}
                                </span>
                                <span className='activity-message'>{mention.text}</span>
                                <span className='activity-date'>{formatDateTime(mention.created, userLocale, userTimezone)}</span>
                            </div>
                            <div
                                className='activity-dismiss'
                                title={t('notifications.general.dismiss_label')}
                                onClick={(event) => { event.stopPropagation(); dismissMention(mention); }}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {notifications.length > 0 && (
                <div className='activity-timeline'>
                    {notifications.map(notification => (
                        <div className='activity-item notification no-drag' key={`notification-${notification.id}`} onClick={() => openNotification(notification)}>
                            <div className='activity-marker warning'>
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                            </div>
                            <div className='activity-content'>
                                <span className='activity-title'>{t(`notifications.${notification.type}.title`)}</span>
                                <span className='activity-message'>{t(`notifications.${notification.type}.message`, notification.data)}</span>
                                <span className='activity-date'>{formatDateTime(notification.created, userLocale, userTimezone)}</span>
                            </div>
                            <div
                                className='activity-dismiss'
                                title={t('notifications.general.dismiss_label')}
                                onClick={(event) => dismissNotification(event, notification.id)}
                            >
                                <FontAwesomeIcon icon={faXmark} />
                            </div>
                        </div>
                    ))}
                </div>
            )}
            {updates.length === 0 && notifications.length === 0 && mentions.length === 0 ? (
                <div className='metric-chart-empty'>{t('general.no_data_available')}</div>
            ) : (
                <div className='activity-timeline'>
                    {updates.map(update => (
                        <div className='activity-item no-drag' key={`update-${update.id}`} onClick={() => openPlatformUpdate(update)}>
                            <div className='activity-marker'>
                                <FontAwesomeIcon icon={faRocket} />
                            </div>
                            <div className='activity-content'>
                                <span className='activity-title'>{t('dashboard.platform_update.title', { number: update.release_number, date: formatDate(update.sent_at, userLocale, 'writtenMonth') })}</span>
                                {update.subject && <span className='activity-message'>{update.subject}</span>}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default FocusPanelActivity;
